import {
	CHAT_BUBBLES,
	CHAT_BUTTONS,
	CHAT_CORNERS,
	CHAT_FONTS,
	CHAT_HEADERS,
	CHAT_SCHEMES,
	fontFamilySchema,
	hexColorSchema as hexColor,
} from "@repo/embed";
import { z } from "zod";

/**
 * A retailer's own typeface: a woff2 in our storage (`fonts/<org>/<id>.woff2`, uploaded through
 * agentConfig.fontUpload, which checks the bytes) and the name it is shown and declared under.
 * The family is written into an @font-face rule, so it may not hold quotes, semicolons or
 * brackets.
 */
export const customFontSchema = z.object({
	family: fontFamilySchema,
	path: z
		.string()
		.regex(/^fonts\/[\w-]+\/[\w-]+\.woff2$/, "Upload the font again"),
});

/**
 * The hosted chat in the retailer's colours. The logo is the organization's
 * own; everything else a retailer changes lives here, not in code. Defaults
 * are neutral ink so an unbranded retailer still looks finished.
 */
export const appearanceSchema = z
	.object({
		/** Header, the shopper's bubbles, primary buttons. */
		primaryColor: hexColor.default("#1C1917"),
		/** Where the primary deepens: header gradient end, live voice backdrop. */
		deepColor: hexColor.default("#0C0A09"),
		/** Sparingly: added, live and positive states. Needs dark text on it. */
		accentColor: hexColor.default("#D9F99D"),
		/** Discounted prices and offer badges. */
		dealColor: hexColor.default("#15803D"),
		/** One line under the assistant's name in the header. */
		tagline: z.string().max(60).default(""),
		/** Latin typeface of the chat; Arabic script stays IBM Plex Sans Arabic. */
		font: z.enum(CHAT_FONTS).default("geist"),
		/** Replaces `font` for Latin text when set. */
		customFont: customFontSchema.optional(),
		/** Light, dark, or the shopper's device setting. */
		scheme: z.enum(CHAT_SCHEMES).default("light"),
		/** How round bubbles, cards and tiles are. */
		corners: z.enum(CHAT_CORNERS).default("round"),
		/** The header on the page's surface, or filled with the brand colour. */
		header: z.enum(CHAT_HEADERS).default("plain"),
		/** The shopper's own messages: filled with the brand colour, or a tint of it. */
		bubble: z.enum(CHAT_BUBBLES).default("brand"),
		/** The shape of buttons: send, mic, add, view basket, checkout. */
		buttons: z.enum(CHAT_BUTTONS).default("pill"),
		/**
		 * One part's own colour, when it should not follow the four above. Absent: derived.
		 * The text on each is picked for contrast, so no choice here can be unreadable.
		 */
		ctaColor: hexColor.optional(),
		headerColor: hexColor.optional(),
		bubbleColor: hexColor.optional(),
		surfaceColor: hexColor.optional(),
		/** Cards and sheets, text, secondary text, lines and borders. */
		cardColor: hexColor.optional(),
		textColor: hexColor.optional(),
		mutedColor: hexColor.optional(),
		lineColor: hexColor.optional(),
		/**
		 * A typeface the shopper's device already has, ahead of `font`. An app sets it
		 * (`fontFamily` override) so the chat reads like the app around it.
		 */
		deviceFont: fontFamilySchema.optional(),
		/**
		 * The retailer's app or site may restyle the chat it opens (`@repo/embed`
		 * appearanceOverridesSchema: scheme, colours, corners, header).
		 */
		allowAppOverrides: z.boolean().default(true),
	})
	.prefault({});
